/**
 * Single source of truth for red/green number semantics on comp percentages
 * (and any signed performance ratio rendered as a percent).
 *
 * A comp value is a decimal ratio: 0.05 = +5%, -0.226 = -22.6%.
 *
 * The thresholds align with the severity tiers in useRetailStores.ts
 * (severityLabel / SEVERITY_ORDER) so a number's color agrees with the
 * severity ring next to it:
 *   - watch tier starts at -10%
 *   - critical tier starts at -30%
 *
 * Grading:
 *   >=  0        green-700 (light) / green-400 (dark)            semibold
 *   0 to -10%    ink (neutral; a small dip is not alarming)
 *   -10% to -30% red-700 (light) / red-400 (dark)                semibold
 *   <  -30%      red-800 (light) / red-300 (dark)                bold
 *
 * Returns the Tailwind class string. null/undefined renders neutral ink.
 */
export function compColorClass(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) {
    return 'text-amp-ink dark:text-amp-dark-ink';
  }
  if (v >= 0) {
    return 'text-green-700 dark:text-green-400 font-semibold';
  }
  if (v > -0.1) {
    // Mild dip: neutral, no red.
    return 'text-amp-ink dark:text-amp-dark-ink';
  }
  if (v >= -0.3) {
    // -10% to -30%: pressure / watch band.
    return 'text-red-700 dark:text-red-400 font-semibold';
  }
  // < -30%: critical band.
  return 'text-red-800 dark:text-red-300 font-bold';
}
